import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Loader2, UserPlus, ArrowRightLeft, UserMinus } from "lucide-react";
import { toast } from "sonner";
import TerritoryLabel from "../../components/common/TerritoryLabel";

/**
 * The three team-change dialogs (#15): induct, reassign, release.
 *
 * All three POST /team-changes and let the backend decide the rest. For a
 * Sales Manager or the Admin Manager the response comes back `Submitted` — the
 * change happens when an approver decides it. For Tech Admin and the Director
 * the same POST comes back `Approved` and already applied, so the confirmation
 * copy must not promise an approval step their action skips.
 */

function submittedToast(change) {
  if (change.status === "Approved") {
    toast.success("Applied — you hold direct authority, no approval step");
  } else {
    // NOT the Tech Admin. #111 removed him from every chain, and
    // `_single_stage_filter` no longer puts a team change in his queue — a
    // toast naming him would send the filer to somebody who cannot decide it.
    toast.success("Sent for approval — Admin Manager or Director");
  }
}

async function post(payload) {
  const { data } = await api.post("/team-changes", payload);
  return data;
}

const errText = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;

export function InductDialog({ open, onClose, viewer, onDone }) {
  const [officers, setOfficers] = useState(null); // null = loading
  const [soId, setSoId] = useState("");
  const [toManagerId, setToManagerId] = useState("");
  const [managers, setManagers] = useState([]);
  const [busy, setBusy] = useState(false);

  // A Sales Manager inducts into their own team, full stop. The company-wide
  // roles choose the receiving manager.
  const choosesManager = viewer?.role !== "assistant_manager";

  useEffect(() => {
    if (!open) return;
    setSoId("");
    setToManagerId("");
    setOfficers(null);
    api.get("/team-changes/unassigned")
      .then(({ data }) => setOfficers(data))
      .catch((e) => { toast.error(errText(e)); setOfficers([]); });
    if (choosesManager) {
      api.get("/team-changes/managers")
        .then(({ data }) => setManagers(data))
        .catch((e) => toast.error(errText(e)));
    }
  }, [open, choosesManager]);

  async function submit() {
    setBusy(true);
    try {
      const change = await post({
        kind: "induct",
        so_id: soId,
        to_manager_id: choosesManager ? toManagerId : undefined,
      });
      submittedToast(change);
      onClose();
      onDone();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="induct-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus size={16} /> Induct a team member
          </DialogTitle>
          <DialogDescription>
            Unassigned Sales Officers — released from a team, or not yet placed.
          </DialogDescription>
        </DialogHeader>

        {officers === null ? (
          <p className="text-sm text-callisto-muted">Loading…</p>
        ) : officers.length === 0 ? (
          <p className="text-sm text-callisto-muted" data-testid="induct-empty">
            Nobody is unassigned right now.
          </p>
        ) : (
          <div className="space-y-3">
            <Select value={soId} onValueChange={setSoId}>
              <SelectTrigger className="h-11 focus:ring-callisto-gold" data-testid="induct-so-select">
                <SelectValue placeholder="Sales Officer" />
              </SelectTrigger>
              <SelectContent>
                {/* The officer's territory is rendered by the one renderer
                    (RC-10), not by a template literal carrying its own
                    separator — the shape that produced ", Patna" and
                    "Cardiology · " elsewhere. `full_name` stays a bare text
                    child so the option remains findable by name alone. */}
                {officers.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.full_name}
                    {o.territory ? (
                      <span className="text-callisto-muted">
                        {" · "}
                        <TerritoryLabel value={o.territory} unmigrated={o.territory_unmigrated} review="icon" />
                      </span>
                    ) : null}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {choosesManager && (
              <Select value={toManagerId} onValueChange={setToManagerId}>
                <SelectTrigger className="h-11 focus:ring-callisto-gold" data-testid="induct-manager-select">
                  <SelectValue placeholder="Into whose team?" />
                </SelectTrigger>
                <SelectContent>
                  {managers.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || !soId || (choosesManager && !toManagerId)}
            className="bg-callisto-navy hover:bg-callisto-navy/90 text-white"
            data-testid="induct-submit"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Induct"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ReassignDialog({ open, officer, onClose, onDone }) {
  const [managers, setManagers] = useState([]);
  const [toManagerId, setToManagerId] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setToManagerId("");
    setReason("");
    api.get("/team-changes/managers")
      .then(({ data }) => setManagers(data))
      .catch((e) => toast.error(errText(e)));
  }, [open]);

  async function submit() {
    setBusy(true);
    try {
      const change = await post({
        kind: "reassign",
        so_id: officer.id,
        to_manager_id: toManagerId,
        reason: reason.trim() || undefined,
      });
      submittedToast(change);
      onClose();
      onDone();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  }

  // The current manager is not a valid destination — that is a no-op the
  // backend would refuse anyway.
  const targets = managers.filter((m) => m.id !== officer?.manager_id);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reassign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft size={16} /> Reassign {officer?.full_name}
          </DialogTitle>
          <DialogDescription>
            Moves this officer to another manager's team, with their doctors,
            visits and history intact.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Select value={toManagerId} onValueChange={setToManagerId}>
            <SelectTrigger className="h-11 focus:ring-callisto-gold" data-testid="reassign-manager-select">
              <SelectValue placeholder="New manager" />
            </SelectTrigger>
            <SelectContent>
              {targets.map((m) => (
                <SelectItem key={m.id} value={m.id}>{m.full_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Reason (optional — shown to the approver)"
            className="focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
            data-testid="reassign-reason"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || !toManagerId}
            className="bg-callisto-navy hover:bg-callisto-navy/90 text-white"
            data-testid="reassign-submit"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reassign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * RELEASE NAMES THE MANAGER TAKING THE OFFICER OVER.
 *
 * It used to name nobody and leave the officer unassigned. #111 took the Tech
 * Admin out of the approval chains — correctly — and that removed the person
 * who had been standing in front of the consequence: an officer with no Sales
 * Manager has no stage-1 approver, so their expenses and leaves are verifiable
 * by nobody at all. This dialog was one click from producing one.
 *
 * The manager list comes from `/team-changes/managers` — the same endpoint the
 * reassign picker reads, which is the endpoint because a Sales Manager's
 * `/users` window is self + reports and cannot see the colleague they are
 * handing the officer to. NO FALLBACK LIST: if it cannot be loaded, the button
 * stays disabled and the reason is on screen. A hardcoded roster here would be
 * a snapshot of the users collection, which is the #78 shape.
 *
 * The backend refuses a release with no `to_manager_id` regardless — this is
 * the same rule said early, not the rule itself.
 */
export function ReleaseDialog({ open, officer, onClose, onDone }) {
  const [managers, setManagers] = useState(null); // null = loading
  const [toManagerId, setToManagerId] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReason("");
    setToManagerId("");
    setManagers(null);
    api.get("/team-changes/managers")
      .then(({ data }) => setManagers(data))
      .catch((e) => { toast.error(errText(e)); setManagers([]); });
  }, [open]);

  // The officer's current manager cannot receive them — that is a no-op, and
  // the backend refuses it.
  const targets = (managers || []).filter((m) => m.id !== officer?.manager_id);

  async function submit() {
    setBusy(true);
    try {
      const change = await post({
        kind: "release",
        so_id: officer.id,
        to_manager_id: toManagerId,
        reason: reason.trim() || undefined,
      });
      submittedToast(change);
      onClose();
      onDone();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="release-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserMinus size={16} /> Release {officer?.full_name}
          </DialogTitle>
          <DialogDescription>
            Hands this officer to another manager's team. Their account, doctors,
            visits and history stay exactly as they are — only the reporting line
            moves.{" "}
            <span className="font-semibold">
              Every Sales Officer must always have a Sales Manager
            </span>
            , because that manager is who verifies their expenses, leaves and
            tour plans at stage 1. So a release names the manager taking them
            over; there is no way to leave them with nobody.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {managers === null ? (
            <p className="text-sm text-callisto-muted" data-testid="release-managers-loading">
              Loading managers…
            </p>
          ) : targets.length === 0 ? (
            // A gap shown as a gap, and the reason named. Silently disabling
            // the button would leave the manager guessing.
            <p className="text-sm text-amber-700 font-semibold" data-testid="release-no-manager-available">
              There is no other active manager to hand {officer?.full_name} to,
              so this release cannot go ahead — an officer must never be left
              without a Sales Manager. Add or reactivate a manager first.
            </p>
          ) : (
            <Select value={toManagerId} onValueChange={setToManagerId}>
              <SelectTrigger className="h-11 focus:ring-callisto-gold" data-testid="release-manager-select">
                <SelectValue placeholder="Who takes them over?" />
              </SelectTrigger>
              <SelectContent>
                {targets.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Reason (optional — shown to the approver)"
            className="focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
            data-testid="release-reason"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || !toManagerId}
            className="bg-orange-600 hover:bg-orange-700 text-white"
            data-testid="release-submit"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Release"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
